import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_SERVICES = [
  { label: 'Vehicle Registration', to: '/services/vehicle-registration', icon: 'ri-car-line', desc: 'New, renewal & out-of-state' },
  { label: 'Title Transfer', to: '/services/title-transfer', icon: 'ri-file-transfer-line', desc: 'Private party & dealer sales' },
  { label: 'License Plates', to: '/services/license-plates', icon: 'ri-bank-card-line', desc: 'Standard, specialty & vanity' },
  { label: 'Duplicate Title', to: '/services/duplicate-title', icon: 'ri-file-copy-line', desc: 'Lost, stolen or damaged' },
  { label: 'Temporary Permits', to: '/services/temporary-permits', icon: 'ri-time-line', desc: '10-day & 30-day permits' },
  { label: 'Notary Services', to: '/services/notary', icon: 'ri-quill-pen-line', desc: 'On-site document notarization' },
];

const NAV_INFO = [
  { label: 'About Us', to: '/about', icon: 'ri-building-line', desc: 'Our story & credentials' },
  { label: 'Fees & Pricing', to: '/fees', icon: 'ri-money-dollar-circle-line', desc: 'Nevada DMV fee breakdown' },
  { label: 'DMV Forms', to: '/forms', icon: 'ri-file-list-3-line', desc: 'All official NV DMV forms' },
  { label: 'FAQ', to: '/faq', icon: 'ri-question-answer-line', desc: 'Common questions answered' },
  { label: 'Resources & Guides', to: '/resources', icon: 'ri-book-open-line', desc: 'Step-by-step guides' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const servicesRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdowns when navigating
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setInfoOpen(false);
  }, [location.pathname]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const isServiceActive = NAV_SERVICES.some((s) => location.pathname === s.to);
  const isInfoActive = NAV_INFO.some((s) => location.pathname === s.to);

  const toggleServices = () => {
    setServicesOpen((prev) => !prev);
    setInfoOpen(false);
  };

  const toggleInfo = () => {
    setInfoOpen((prev) => !prev);
    setServicesOpen(false);
  };

  return (
    <>
      {/* ── ANNOUNCEMENT BAR ── */}
      {announcementVisible && (
        <div className="w-full relative" style={{ backgroundColor: '#E87722' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center h-10 gap-3">
            <i className="ri-shield-star-fill text-white text-sm"></i>
            <p className="text-white text-xs sm:text-sm font-semibold text-center">
              We are NOT the Nevada DMV — Licensed 3rd Party Document Prep Service (NRS240A)
              <a
                href="https://dmv.nv.gov"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="ml-2 underline font-bold text-white/90 hover:text-white whitespace-nowrap"
              >
                Visit Official DMV →
              </a>
            </p>
            <button
              onClick={() => setAnnouncementVisible(false)}
              className="absolute right-4 text-white/70 hover:text-white transition-colors cursor-pointer"
              aria-label="Close announcement"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── TOP UTILITY BAR ── */}
      <div className="w-full hidden md:block" style={{ backgroundColor: '#001f4d' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-9">
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-xs flex items-center gap-1.5">
              <i className="ri-map-pin-fill" style={{ color: '#E87722' }}></i>
              4525 S Sandhill Rd, Las Vegas, NV 89121
            </span>
            <span className="text-white/20 text-xs">|</span>
            <span className="text-white/60 text-xs flex items-center gap-1.5">
              <i className="ri-time-fill" style={{ color: '#E87722' }}></i>
              Mon–Fri 9AM–5PM &nbsp;·&nbsp; Sat 10AM–3PM
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="mailto:info@dmv-xpress.com"
              className="text-white/60 hover:text-white text-xs flex items-center gap-1.5 transition-colors"
            >
              <i className="ri-mail-fill" style={{ color: '#E87722' }}></i>
              info@dmv-xpress.com
            </a>
            <a
              href="tel:7028028696"
              className="flex items-center gap-1.5 text-xs font-bold transition-colors whitespace-nowrap"
              style={{ color: '#E87722' }}
            >
              <i className="ri-phone-fill"></i>
              (702) 802-8696
            </a>
          </div>
        </div>
      </div>

      {/* ── MAIN HEADER ── */}
      <header
        className="w-full z-50 transition-all duration-300"
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: scrolled ? '#fff' : '#002a5c',
          borderBottom: scrolled ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">

            {/* ── LOGO ── */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
              <div
                className="relative w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #E87722 0%, #c45e10 100%)' }}
              >
                <i className="ri-car-fill text-white text-2xl"></i>
                <div
                  className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                  style={{ backgroundColor: '#002a5c' }}
                >
                  <i className="ri-check-fill text-white" style={{ fontSize: '8px' }}></i>
                </div>
              </div>
              <div className="leading-none">
                <div className="flex items-baseline gap-1">
                  <span
                    className="font-black text-2xl tracking-tight"
                    style={{
                      color: scrolled ? '#002a5c' : '#fff',
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '-0.03em',
                    }}
                  >
                    DMV-Xpress
                  </span>
                  <span className="text-sm font-bold" style={{ color: '#E87722' }}>™</span>
                </div>
                <span
                  className="text-xs font-semibold block mt-0.5 tracking-wide uppercase"
                  style={{ color: scrolled ? '#64748b' : 'rgba(255,255,255,0.6)', letterSpacing: '0.08em' }}
                >
                  Services · Las Vegas, NV
                </span>
              </div>
            </Link>

            {/* ── DESKTOP NAV ── */}
            <nav className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
                style={{
                  color: scrolled ? (isActive('/') ? '#002a5c' : '#475569') : (isActive('/') ? '#E87722' : 'rgba(255,255,255,0.85)'),
                  backgroundColor: isActive('/') && scrolled ? '#f1f5f9' : 'transparent',
                }}
              >
                Home
              </Link>

              {/* ── SERVICES DROPDOWN — click to open, click outside to close ── */}
              <div ref={servicesRef} className="relative">
                <button
                  onClick={toggleServices}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                  style={{
                    color: scrolled
                      ? (isServiceActive || servicesOpen ? '#002a5c' : '#475569')
                      : (isServiceActive || servicesOpen ? '#E87722' : 'rgba(255,255,255,0.85)'),
                    backgroundColor: (isServiceActive || servicesOpen) && scrolled ? '#f1f5f9' : 'transparent',
                  }}
                >
                  Services
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {servicesOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 bg-white rounded-2xl overflow-hidden"
                    style={{
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.14)',
                      width: '280px',
                      zIndex: 100,
                    }}
                  >
                    <div className="p-2">
                      <div className="px-3 py-2 mb-1">
                        <p className="text-xs font-black uppercase tracking-widest" style={{ color: '#E87722' }}>Our Services</p>
                      </div>
                      {NAV_SERVICES.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group/item hover:bg-slate-50"
                          style={{ backgroundColor: isActive(s.to) ? '#FFF4EC' : 'transparent' }}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: isActive(s.to) ? '#E87722' : '#f1f5f9' }}
                          >
                            <i className={`${s.icon} text-sm`} style={{ color: isActive(s.to) ? '#fff' : '#E87722' }}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold leading-none mb-0.5" style={{ color: '#1e293b' }}>{s.label}</p>
                            <p className="text-xs" style={{ color: '#94a3b8' }}>{s.desc}</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-300 group-hover/item:text-gray-400 transition-colors flex-shrink-0"></i>
                        </Link>
                      ))}
                    </div>
                    <div className="px-3 py-3 border-t" style={{ borderColor: '#f1f5f9', backgroundColor: '#fafbfc' }}>
                      <a
                        href="tel:7028028696"
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 whitespace-nowrap"
                        style={{ backgroundColor: '#E87722' }}
                      >
                        <i className="ri-phone-fill"></i>Call to Book — (702) 802-8696
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* ── RESOURCES DROPDOWN — click to open, click outside to close ── */}
              <div ref={infoRef} className="relative">
                <button
                  onClick={toggleInfo}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                  style={{
                    color: scrolled
                      ? (isInfoActive || infoOpen ? '#002a5c' : '#475569')
                      : (isInfoActive || infoOpen ? '#E87722' : 'rgba(255,255,255,0.85)'),
                    backgroundColor: (isInfoActive || infoOpen) && scrolled ? '#f1f5f9' : 'transparent',
                  }}
                >
                  Resources
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${infoOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {infoOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 bg-white rounded-2xl overflow-hidden"
                    style={{
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.14)',
                      width: '260px',
                      zIndex: 100,
                    }}
                  >
                    <div className="p-2">
                      <div className="px-3 py-2 mb-1">
                        <p className="text-xs font-black uppercase tracking-widest" style={{ color: '#E87722' }}>Info &amp; Resources</p>
                      </div>
                      {NAV_INFO.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all hover:bg-slate-50"
                          style={{ backgroundColor: isActive(s.to) ? '#FFF4EC' : 'transparent' }}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: isActive(s.to) ? '#E87722' : '#f1f5f9' }}
                          >
                            <i className={`${s.icon} text-sm`} style={{ color: isActive(s.to) ? '#fff' : '#E87722' }}></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold leading-none mb-0.5" style={{ color: '#1e293b' }}>{s.label}</p>
                            <p className="text-xs" style={{ color: '#94a3b8' }}>{s.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/contact"
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
                style={{
                  color: scrolled ? (isActive('/contact') ? '#002a5c' : '#475569') : (isActive('/contact') ? '#E87722' : 'rgba(255,255,255,0.85)'),
                  backgroundColor: isActive('/contact') && scrolled ? '#f1f5f9' : 'transparent',
                }}
              >
                Contact
              </Link>
            </nav>

            {/* ── CTA BUTTONS ── */}
            <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
              <a
                href="tel:7028028696"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap border"
                style={{
                  color: scrolled ? '#002a5c' : '#fff',
                  borderColor: scrolled ? '#002a5c' : 'rgba(255,255,255,0.35)',
                  backgroundColor: 'transparent',
                }}
              >
                <i className="ri-phone-fill"></i>
                (702) 802-8696
              </a>
              <a
                href="tel:7028028696"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all whitespace-nowrap hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #E87722 0%, #c45e10 100%)' }}
              >
                <i className="ri-calendar-check-fill"></i>
                Book Appointment
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg transition-colors cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                color: scrolled ? '#002a5c' : '#fff',
                backgroundColor: scrolled ? '#f1f5f9' : 'rgba(255,255,255,0.12)',
              }}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* ── MOBILE MENU ── */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t" style={{ borderColor: '#e2e8f0' }}>
            <div className="max-w-7xl mx-auto px-4 py-5 space-y-1">
              <Link to="/" className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold" style={{ color: '#002a5c' }}>
                <i className="ri-home-line" style={{ color: '#E87722' }}></i>Home
              </Link>

              <div className="pt-2">
                <p className="text-xs font-black uppercase tracking-widest px-3 mb-2" style={{ color: '#E87722' }}>Our Services</p>
                {NAV_SERVICES.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-colors"
                    style={{ color: isActive(s.to) ? '#002a5c' : '#475569', backgroundColor: isActive(s.to) ? '#FFF4EC' : 'transparent' }}
                  >
                    <i className={`${s.icon} text-sm`} style={{ color: '#E87722' }}></i>
                    <span className="text-sm font-medium">{s.label}</span>
                  </Link>
                ))}
              </div>

              <div className="pt-2">
                <p className="text-xs font-black uppercase tracking-widest px-3 mb-2" style={{ color: '#E87722' }}>Info &amp; Resources</p>
                {NAV_INFO.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-colors"
                    style={{ color: isActive(s.to) ? '#002a5c' : '#475569', backgroundColor: isActive(s.to) ? '#FFF4EC' : 'transparent' }}
                  >
                    <i className={`${s.icon} text-sm`} style={{ color: '#E87722' }}></i>
                    <span className="text-sm font-medium">{s.label}</span>
                  </Link>
                ))}
              </div>

              <Link to="/contact" className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold" style={{ color: '#002a5c' }}>
                <i className="ri-mail-line" style={{ color: '#E87722' }}></i>Contact
              </Link>

              <div className="pt-3 space-y-2">
                <a
                  href="tel:7028028696"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm border-2 whitespace-nowrap"
                  style={{ color: '#002a5c', borderColor: '#002a5c' }}
                >
                  <i className="ri-phone-fill"></i>(702) 802-8696
                </a>
                <a
                  href="tel:7028028696"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-bold text-sm whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #E87722 0%, #c45e10 100%)' }}
                >
                  <i className="ri-calendar-check-fill"></i>Book Appointment
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
